import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { getOrderReceipt } from '../services/skipcash';
import { useCart } from '../context/CartContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './Receipt.css';

function Receipt() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId: urlParam } = useParams();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const receiptRef = useRef(null);

  // Get token or orderId from URL params (token is preferred for security)
  const rawToken = urlParam || searchParams.get('token');
  // Normalize placeholder tokens like {receiptToken} that may come from mis-linked URLs
  const receiptToken = rawToken === '{receiptToken}' || rawToken === '%7BreceiptToken%7D' ? null : rawToken;
  const fallbackOrderId = searchParams.get('orderId') || location.state?.orderId;

  useEffect(() => {
    const fetchOrder = async () => {
      // Try to find order by receiptToken first (secure), then fallback to orderId
      let tokenToUse = receiptToken;
      let orderIdToUse = fallbackOrderId;
      
      // Check sessionStorage for order info
      const pendingOrder = sessionStorage.getItem('pendingOrder');
      if (pendingOrder) {
        try {
          const pending = JSON.parse(pendingOrder);
          if (!tokenToUse) tokenToUse = pending.receiptToken;
          if (!orderIdToUse) orderIdToUse = pending.orderId;
        } catch (e) {
          console.error('Error parsing pending order:', e);
        }
      }
      
      // Also check localStorage for order info (more persistent)
      if (!tokenToUse && !orderIdToUse) {
        const keys = Object.keys(localStorage).filter(key => key.startsWith('order_'));
        if (keys.length > 0) {
          const recentOrders = keys.map(key => {
            try {
              const orderInfo = JSON.parse(localStorage.getItem(key));
              return { ...orderInfo, key };
            } catch (e) {
              return null;
            }
          }).filter(Boolean).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          
          if (recentOrders.length > 0) {
            if (!tokenToUse) tokenToUse = recentOrders[0].receiptToken;
            if (!orderIdToUse) orderIdToUse = recentOrders[0].orderId;
          }
        }
      }

      if (!tokenToUse && !orderIdToUse) {
        setError('Receipt not found. Please check your order confirmation email or contact support.');
        setIsLoading(false);
        return;
      }

      try {
        if (!tokenToUse) {
          setError('Receipt token is missing. Please contact support.');
          setIsLoading(false);
          return;
        }

        const receiptResponse = await getOrderReceipt(tokenToUse);
        if (!receiptResponse?.order) {
          if (receiptResponse?.status === 'pending') {
            setError('Payment is still pending. Please check back shortly.');
            setIsLoading(false);
            return;
          }
          setError('Order not found. Please contact support.');
          setIsLoading(false);
          return;
        }

        const orderData = {
          id: receiptResponse.order.id,
          ...receiptResponse.order,
        };
        
        // Validate order data
        if (!orderData.customer || !orderData.items || !orderData.totals) {
          setError('Order data is incomplete. Please contact support.');
          setIsLoading(false);
          return;
        }
        
        setOrder(orderData);
        
        // Clear cart if payment is successful
        if (orderData.paymentStatus === 'paid') {
          clearCart();
          sessionStorage.removeItem('pendingOrder');
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details. Please try again.');
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [receiptToken, fallbackOrderId, searchParams, clearCart]);

  if (isLoading) {
    return (
      <div className="receipt-page">
        <div className="receipt-container">
          <div className="receipt-loading">
            <div className="spinner"></div>
            <p>Loading receipt...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    const tokenForProcessing = receiptToken;
    const processingParams = new URLSearchParams();
    if (tokenForProcessing) processingParams.set('token', tokenForProcessing);
    if (fallbackOrderId) processingParams.set('orderId', fallbackOrderId);
    const processingUrl = `/processing-payment${processingParams.toString() ? `?${processingParams.toString()}` : ''}`;

    return (
      <div className="receipt-page">
        <div className="receipt-container">
          <div className="receipt-error">
            <h2>{error?.includes('pending') ? 'Payment Pending' : 'Order Not Found'}</h2>
            <p>{error || 'The order you are looking for could not be found.'}</p>
            <div className="receipt-actions">
              {error?.includes('pending') && (
                <button className="btn-primary" onClick={() => navigate(processingUrl)}>
                  Check Payment Status
                </button>
              )}
              <button className="btn-outline" onClick={() => navigate('/')}>
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    let date = null;

    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp?.toDate === 'function') {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'object') {
      const seconds = typeof timestamp.seconds === 'number' ? timestamp.seconds : timestamp._seconds;
      const nanos = typeof timestamp.nanoseconds === 'number' ? timestamp.nanoseconds : timestamp._nanoseconds;
      if (typeof seconds === 'number') {
        date = new Date(seconds * 1000 + Math.floor((nanos || 0) / 1e6));
      } else if (typeof timestamp.iso === 'string') {
        date = new Date(timestamp.iso);
      } else if (typeof timestamp.date === 'string') {
        date = new Date(timestamp.date);
      }
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp);
    }

    if (!date || Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDownloadPDF = async () => {
    if (!receiptRef.current || !order) return;
    
    setIsGeneratingPDF(true);
    try {
      // Hide buttons temporarily for PDF
      const actionsElement = receiptRef.current.querySelector('.receipt-actions');
      const originalDisplay = actionsElement?.style.display;
      if (actionsElement) {
        actionsElement.style.display = 'none';
      }

      // Generate canvas from HTML
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      // Restore buttons
      if (actionsElement) {
        actionsElement.style.display = originalDisplay || '';
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgScaledWidth = imgWidth * ratio;
      const imgScaledHeight = imgHeight * ratio;
      
      // Center the image
      const xOffset = (pdfWidth - imgScaledWidth) / 2;
      const yOffset = (pdfHeight - imgScaledHeight) / 2;

      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgScaledWidth, imgScaledHeight);
      
      // Generate filename
      const orderId = order.orderId || order.id || 'receipt';
      const filename = `Receipt-${orderId}.pdf`;
      
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const customer = order.customer || {};
  const shipping = order.shipping || {};
  const totals = order.totals || {};
  const items = order.items || [];

  return (
    <div className="receipt-page">
      <div className="receipt-container" ref={receiptRef}>
        <div className="receipt-header">
          <div className="receipt-logo">
            <h1>THE TAG</h1>
          </div>
          <div className="receipt-title">
            <h2>Order Receipt</h2>
            <p className="receipt-order-id">Order #{order.orderId || order.id}</p>
          </div>
        </div>

        <div className="receipt-content">
          <div className="receipt-section">
            <h3>Order Details</h3>
            <div className="receipt-details-grid">
              <div>
                <span className="label">Order Date:</span>
                <span className="value">{formatDate(order.createdAt)}</span>
              </div>
              <div>
                <span className="label">Status:</span>
                <span className={`value status-badge status-${order.status || 'pending'}`}>
                  {order.status || 'Pending'}
                </span>
              </div>
              <div>
                <span className="label">Payment Status:</span>
                <span className={`value status-badge status-${order.paymentStatus || 'pending'}`}>
                  {order.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </div>
              {order.paymentMethod && (
                <div>
                  <span className="label">Payment Method:</span>
                  <span className="value">
                    {order.paymentMethod === 'skipcash' ? 'SkipCash' : 
                     order.paymentMethod === 'cash' ? 'Cash on Delivery' : 
                     order.paymentMethod}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="receipt-section">
            <h3>Customer Information</h3>
            <div className="receipt-info">
              <p><strong>{customer.firstName} {customer.lastName}</strong></p>
              <p>{customer.email}</p>
              {customer.phone && <p>{customer.phone}</p>}
            </div>
          </div>

          {shipping && Object.keys(shipping).length > 0 && (
            <div className="receipt-section">
              <h3>Shipping Address</h3>
              <div className="receipt-info">
                {shipping.buildingNumber && <p>Building {shipping.buildingNumber}</p>}
                {shipping.streetNumber && <p>Street {shipping.streetNumber}</p>}
                {shipping.zoneNumber && <p>Zone {shipping.zoneNumber}</p>}
                {shipping.city && <p>{shipping.city}</p>}
                {shipping.country && <p>{shipping.country}</p>}
                {shipping.postalCode && <p>{shipping.postalCode}</p>}
              </div>
            </div>
          )}

          <div className="receipt-section">
            <h3>Order Items</h3>
            <div className="receipt-items">
              <table className="receipt-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Size</th>
                    <th>Length</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.size || 'N/A'}</td>
                      <td>{item.length || 'N/A'}</td>
                      <td>{item.quantity || 1}</td>
                      <td>{item.price?.toFixed(2) || '0.00'} ر.ق</td>
                      <td>{((item.price || 0) * (item.quantity || 1)).toFixed(2)} ر.ق</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="receipt-section receipt-totals">
            <div className="receipt-total-row">
              <span className="label">Subtotal:</span>
              <span className="value">{totals.subtotal?.toFixed(2) || '0.00'} ر.ق</span>
            </div>
            <div className="receipt-total-row">
              <span className="label">Shipping:</span>
              <span className="value">{totals.shipping?.toFixed(2) || '0.00'} ر.ق</span>
            </div>
            <div className="receipt-total-row receipt-total-final">
              <span className="label">Total:</span>
              <span className="value">{totals.total?.toFixed(2) || '0.00'} ر.ق</span>
            </div>
          </div>
        </div>

        <div className="receipt-footer">
          <p className="receipt-thank-you">Thank you for your purchase!</p>
          <p className="receipt-note">
            You will receive an email confirmation with your order details shortly.
          </p>
          <div className="receipt-actions">
            <button 
              className="btn-primary" 
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
            >
              {isGeneratingPDF ? 'Generating PDF...' : 'Download as PDF'}
            </button>
            <button className="btn-secondary" onClick={() => navigate('/shop')}>
              Continue Shopping
            </button>
            <button className="btn-outline" onClick={() => navigate('/')}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Receipt;
